// @ts-nocheck
import _ from 'lodash';
import moment from 'moment';
import { productCodes, formArrayTypeDivider, emsMainCodeList } from '../constants/formConstants';

const DATE_FORMAT: string = 'YYYY-MM-DD';

export function transformToArray(data) {
  let fieldsToTransform;

  const transformedData = _.chain(data)
    .keys()
    .filter((key) => {
      return key.split(formArrayTypeDivider).length === 3;
    })
    .tap((toTransform) => {
      fieldsToTransform = toTransform;
    })
    .map((fullFieldName) => {
      const [arrayName, type, fieldName] = fullFieldName.split(formArrayTypeDivider);
      return { arrayName, type, fieldName, fullFieldName };
    })
    .groupBy((item) => `${item.arrayName}.${item.type}`)
    .reduce((acc, item, key) => {
      const [arrayName, type] = key.split('.');
      const arrayRow = item.reduce(
        (acc, rowItem) => ({ ...acc, [rowItem.fieldName]: data[rowItem.fullFieldName] }),
        {}
      );
      const transformedArrayRow = acc[arrayName]
        ? acc[arrayName].concat({ ...arrayRow, type })
        : [{ ...arrayRow, type }];
      return !['products', 'futureProducts'].includes(arrayName) ||
        (['products', 'futureProducts'].includes(arrayName) && Object.keys(arrayRow).length > 1)
        ? { ...acc, [arrayName]: transformedArrayRow }
        : acc;
    }, {})
    .value();
  return { fieldsToTransform, transformedData };
}

export function combineFieldArrays(data) {
  let fieldToCombine;

  const combinedData = _.chain(data)
    .keys()
    .filter((key) => key.includes(formArrayTypeDivider) && key.split(formArrayTypeDivider).length === 2)
    .tap((toCombine) => {
      fieldToCombine = toCombine;
    })
    .reduce((acc, field) => {
      const [arrayName, type] = field.split(formArrayTypeDivider);
      if (Array.isArray(data[field]) && !data[field].filter((x) => !!x).length) return acc;
      if (acc[arrayName]) {
        return { ...acc, [arrayName]: [...acc[arrayName], ...data[field].map((item) => ({ type, ...item }))] };
      }
      return { ...acc, [arrayName]: [...data[field].map((item) => ({ type, ...item }))] };
    }, {})
    .omit(fieldToCombine)
    .value();

  return _.chain(data).omit(fieldToCombine).assign(combinedData).value();
}

const transformProducts = (products) => {
  let bhRow;
  let fields;

  const transformedBH = _.chain(products)
    .remove({ code: productCodes.bh })
    .first()
    .tap((bh) => (bhRow = bh))
    .keys()
    .tap(() => (fields = ['BHINIT', 'BHMD', 'BHNPT']))
    .filter((item) => fields.includes(item))
    .map((key) => {
      const type = key;
      return { ..._.omit(bhRow, fields), member_copay: bhRow[key], type };
    })
    .value();

  const transformedEMS = _.chain(products)
    .remove({ code: productCodes.ems })
    .first()
    // @ts-ignore
    .thru((item) => {
      if (!item) {
        return [];
      }
      const emsTypes = [...emsMainCodeList];
      const emsType = item.isEfb ? { type: 'EFB' } : {};
      return emsTypes.map((code) => ({ ..._.omit(item, ['isEfb']), code, ...emsType }));
    })
    .value();

  return [...products, ...transformedBH, ...transformedEMS].map((product) => ({
    // A Base Product Configuration
    ...{ member_copay: 0, cost_per_person: 0, could_be_less: false, app_opt_out: false, code: null, type: undefined },
    // Product Data
    ...product,
    // Special handling for dates
    ...{
      start_dt: moment(product.start_dt).format(DATE_FORMAT),
      end_dt:
        product.isEnabled === 'false' && product.start_dt && !product.end_dt
          ? moment().format(DATE_FORMAT)
          : product.end_dt
          ? moment(product.end_dt).format(DATE_FORMAT)
          : null
    }
  }));
};

const reorganizeProducts = (products, could_be_less, app_opt_out) => {
  return Object.entries(products).reduce((p, [key, val]) => {
    // @ts-ignore
    if (val.end_dt || val.start_dt) {
      // @ts-ignore
      delete val.type;
      // @ts-ignore
      const productData = { ...val, code: key, could_be_less, member_copay: val.member_copay || 0 };
      // Only add app_opt_out for GM products
      if (key === 'GM') {
        productData.app_opt_out = app_opt_out;
      }
      p.push(productData);
    }
    return p;
  }, []);
};

const transformCodeDates = (codes) =>
  codes.map(({ code, start_dt, end_dt }) => ({
    code,
    start_dt: moment(start_dt).format(DATE_FORMAT),
    end_dt: end_dt ? moment(end_dt).format(DATE_FORMAT) : null
  }));

const transformCodes = (codes) => {
  return codes.reduce((acc, { code, classCodes, bpCodes }) => {
    if (code || (classCodes && classCodes.length) || (bpCodes && bpCodes.length)) {
      acc.push({ code, classCodes: transformCodeDates(classCodes), bpCodes: transformCodeDates(bpCodes) });
    }
    return acc;
  }, []);
};

export const transformGroups = (data, isPublish = true) => {
  data.start_dt = data.start_dt ? moment(data.start_dt).format(DATE_FORMAT) : null;
  data.end_dt = data.end_dt ? moment(data.end_dt).format(DATE_FORMAT) : null;

  _.remove(data?.groups, _.isUndefined);
  _.forEach(data?.groups, (group) => {
    group.start_dt = group.start_dt ? moment(group.start_dt).format(DATE_FORMAT) : null;
    group.end_dt = group.end_dt ? moment(group.end_dt).format(DATE_FORMAT) : null;

    const productCouldBeLess = group.products.could_be_less === 'true' || false;
    const futureCouldBeLess = group.futureProducts?.could_be_less === 'true' || false;
    // app_opt_out is GM-specific, extract from GM product data
    const productAppOptOut = group.products.GM?.app_opt_out || false;
    const futureAppOptOut = group.futureProducts?.GM?.app_opt_out || false;

    group.products =
      group?.products && transformProducts(reorganizeProducts(group.products, productCouldBeLess, productAppOptOut));
    group.futureProducts =
      group?.futureProducts &&
      transformProducts(reorganizeProducts(group.futureProducts, futureCouldBeLess, futureAppOptOut));

    group.planCodes = transformCodes(group.planCodes);
  });

  // If we have any contacts that are just the type, then remove them
  if (data.contacts) {
    data.contacts = Object.entries(data.contacts)
      .reduce((acc, [key, value]) => {
        if (key === 'Marketing') {
          // @ts-ignore
          value.forEach((contact) => acc.push({ ...filterEmptyObjKeys(contact), type: key }));
        } else {
          // @ts-ignore
          acc.push({ ...filterEmptyObjKeys(value), type: key });
        }
        return acc;
      }, [])
      .filter(({ first, last, email }) => !isPublish || (first && last && email));
  }
};

export const omitExtraFields = (data, allowed) => {
  const simpleFields = _.chain(allowed)
    .omitBy((item) => _.isObjectLike(item))
    .keys()
    .value();
  const complexFields = _.omit(allowed, simpleFields);
  const keyReducer = _.keys(complexFields).reduce((acc, key) => {
    if (_.isArray(allowed[key])) {
      const keyContent = data[key]?.map((ddd) => {
        return omitExtraFields(ddd, _.first(allowed[key]));
      });

      return { ...acc, [key]: keyContent };
    }

    return { ...acc, [key]: omitExtraFields(data[key], allowed[key]) };
  }, {});
  return {
    ..._.pick(data, simpleFields),
    ...keyReducer
  };
};

const filterEmptyObjKeys = (data) =>
  _.flow([Object.entries, (a) => a.filter(([_k, v]) => v), Object.fromEntries])(data);
